import { assert } from '@varos/util-typescript';
import { ReportingShellLoaderBaseConfig } from '../../base';
import { ReportingRootLoader } from './reportingRootlInterface';
import { useMemo } from 'react';
import { ReportingAccountAggregate } from '../../view';
// Import report and chapter data
import {
    report,
    author,
    executiveSummary,
    benchmarks,
    // Add other chapters if more notifications are desired
} from '../../../../route/reporting/fixture/report/developer';

export function createReportingRootLoader(
    config: ReportingShellLoaderBaseConfig
): ReportingRootLoader {
    const {
        repository,
        kernel: {
            infra: { formatter },
        },
    } = config;

    return {
        useLoad(context, props) {
            const meQuery = repository.me.useLookup(context, {
                suspense: true,
                staleTime: Infinity,
            });

            assert(meQuery.status === 'success', 'expected suspense');
            const accountQuery = repository.account.useLookup(
                context,
                {
                    id: meQuery.data.account.id,
                },
                {
                    suspense: true,
                    staleTime: Infinity,
                }
            );

            assert(accountQuery.status === 'success', 'expected suspense');

            const account = useMemo(
                (): ReportingAccountAggregate => ({
                    me: meQuery.data,
                    account: accountQuery.data,
                }),
                [meQuery.data, accountQuery.data]
            );

            // Helper to format time ago or fallback
            const formatTime = (date: Date): string => {
                try {
                    return formatter
                        ? formatter.timeago(date)
                        : date.toLocaleDateString();
                } catch (e) {
                    console.error('Error formatting date:', e);
                    return date.toLocaleDateString(); // Safer fallback
                }
            };

            // Generate notifications with simpler titles and detailed descriptions
            const notificationItems = [
                {
                    id: report.id,
                    title: 'Report Updated',
                    description: `${author.name} updated "${report.title}" ${formatTime(report.editedAt)}`,
                    createdAt: report.editedAt,
                },
                {
                    id: executiveSummary.id,
                    title: 'Chapter Updated',
                    description: `${author.name} updated "${executiveSummary.caption}" ${formatTime(executiveSummary.editedAt)}`,
                    createdAt: executiveSummary.editedAt,
                },
                {
                    id: benchmarks.id,
                    title: 'Chapter Updated',
                    description: `${author.name} updated "${benchmarks.caption}" ${formatTime(benchmarks.editedAt)}`,
                    createdAt: benchmarks.editedAt,
                },
            ].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime()); // Sort by most recent first

            return {
                account,
                notification: {
                    items: notificationItems,
                },
            };
        },
    };
}
